import { useMemo } from 'react';

import { getDisabledReason } from '@frontend/constants';
import { useOperations } from '@frontend/hooks';

/**
 * Hook to get specialized agent permissions.
 * Returns permission flags for specialized agent management.
 *
 * This hook is used across specialized agent components for:
 * - Determining if forms should be read-only
 * - Showing/hiding action buttons (duplicate, delete, etc.)
 * - Controlling access to agent operations
 * - Version history operations (duplicate, restore)
 */
export function useSpecializedAgentPermissions() {
  const { canUser, canInstance } = useOperations();

  return useMemo(() => {
    // User-level permissions
    const canManageSpecializedAgents = canUser(['manage_specialized_agents']);
    const canViewSpecializedAgents = canUser(['view_agents']);

    // Instance-level capabilities
    const hasAgentCreationCapability = canInstance(['agent_creation_enabled']);

    // Composite/calculated permissions for specialized agents
    // Create: Requires both instance capability AND user permission
    const canCreateSpecializedAgents = hasAgentCreationCapability && canManageSpecializedAgents;

    // Edit: Same as create (requires both capability and permission)
    const canEditSpecializedAgents = canCreateSpecializedAgents;

    // Delete: Only requires user permission (allowed even when capability is OFF for cleanup)
    const canDeleteSpecializedAgents = canManageSpecializedAgents;

    // Install from directory: Only requires user permission (same as delete)
    const canInstallSpecializedAgents = canManageSpecializedAgents;

    // View: Requires view_agents permission (users can see agents after tier downgrade if they have this permission)

    return {
      canManageSpecializedAgents,
      hasAgentCreationCapability,
      canCreateSpecializedAgents,
      canEditSpecializedAgents,
      canDeleteSpecializedAgents,
      canInstallSpecializedAgents,
      canViewSpecializedAgents,
      createAgentDisabledReason: getDisabledReason(
        hasAgentCreationCapability,
        canManageSpecializedAgents
      ),
      editAgentDisabledReason: getDisabledReason(
        hasAgentCreationCapability,
        canManageSpecializedAgents
      ),
      deleteAgentDisabledReason: getDisabledReason(true, canManageSpecializedAgents),
    };
  }, [canUser, canInstance]);
}
